import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";

export default function Login() {
  const navigate = useNavigate();
  const { login: adminLogin } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const result = await adminLogin(email, password);
      if (result.success) {
        setLoading(false);
        navigate("/admin/dashboard", { replace: true });
        return;
      } else {
        setError(result.error || "The provided credentials are incorrect.");
      }
    } catch (error) {
      setError("Login failed. Please try again.");
    }
    setLoading(false);
  };

  return (
    <>
      {/* Theme Toggle */}
      <button
        onClick={toggleTheme}
        className={`fixed top-6 right-6 z-[9999] p-3 rounded-xl transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-yellow-400' : 'bg-blue-100 hover:bg-blue-200 text-blue-600 border border-blue-300'} shadow-lg`}
      >
        <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'} text-lg`}></i>
      </button>

      <div className={`h-screen flex flex-col md:flex-row ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'}`}>
        {/* Left Side - Image */}
        <div className={`md:w-1/2 relative overflow-hidden ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-100 to-indigo-200'} hidden md:block`}>
          <div className={`absolute inset-0 z-10 ${isDark ? 'bg-gradient-to-br from-gray-800/95 to-gray-900/95' : 'bg-gradient-to-br from-blue-600/80 to-indigo-700/80'}`}></div>
          <img
            src="https://i.imgur.com/lK6Kurc.jpeg"
            alt="Modern Apartment Building"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 z-20 flex flex-col justify-center items-center text-white p-12">
            <div className="text-center">
              <div className="w-24 h-24 bg-white rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-2xl border border-white/20">
                <img
                  src="/logo.png"
                  alt="Logo"
                  className="w-16 h-16 object-contain"
                />
              </div>
              <h1 className="text-5xl font-bold mb-4">
                Selamat Datang
              </h1>
              <p className="text-xl text-white/90 mb-8 max-width-md">
                Platform admin terpercaya untuk mengelola bisnis sewa apartemen dengan teknologi modern dan aman
              </p>
              <div className="flex items-center justify-center space-x-8 text-white/80">
                <div className="text-center">
                  <div className="text-3xl font-bold bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent">150+</div>
                  <div className="text-sm font-medium">Aman & Terpercaya</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent">2500+</div>
                  <div className="text-sm font-medium">Penyewa Puas</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent">24/7</div>
                  <div className="text-sm font-medium">Dukungan Teknis</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className={`w-full md:w-1/2 flex items-center justify-center p-4 sm:p-6 md:p-8 ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-white to-blue-50'}`}>
          <div className="w-full max-w-sm md:max-w-md">
            <div className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg border`}>
              {/* Header */}
              <div className="text-center mb-8">
                <div className={`w-16 h-16 ${isDark ? 'bg-gray-700' : 'bg-blue-100'} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <img
                    src="/logo.png"
                    alt="Logo"
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <h2 className={`text-2xl sm:text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Admin Login</h2>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Masuk ke dashboard admin Anda</p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleLogin}>
                <div className="mb-4">
                  <label className={`block mb-2 text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
                    placeholder="admin@example.com"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className={`block mb-2 text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
                    placeholder="••••••••"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Logging in...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sign-in-alt mr-2"></i>
                      Login
                    </>
                  )}
                </button>
              </form>

              <div className="mt-8 text-center">
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  © 2024 Admin Panel. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
